import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { ArrowUpward } from "@mui/icons-material";
import { Box, TableSortLabel } from "@mui/material";
import { config } from "../../style";
import SkeletonUI from "./SkeletonUI";

const columnsData = [
    {
        fieldName: "id",
        name: "ID",

        hideCellValue: true,

        renderElement: (row, currentCellValue) => {
            return (
                <span>
                    {row.firstName}
                    <button>hello {currentCellValue}</button>
                </span>
            );
        },
    },
    { fieldName: "firstName", name: "First name" },
    { fieldName: "lastName", name: "Last name" },
    {
        fieldName: "age",
        name: "Age",
        type: "number",
        showSortIcon: true,
    },
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function CustomiseTable({
    columns = [...columnsData],
    rows = [],
    componentStyleProps = {
        tableHeadSx: {},
        tableHeaderCellSx: {},
        tableBodySx: {},
        tableBodyCellSx: {},
        tableSx: {},
        tableContainerSx: {},
    },
    sx = {},
    hoverEffect = true,
    onRowClickHandler = () => {},
    showSkeleton=false
}) {
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("");

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const visibleRows = React.useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);

    if(showSkeleton){
        return <SkeletonUI componentStyleProps={componentStyleProps} sx={sx} columns={columns} rows={rows} />;
    }
    return (
        <TableContainer
            component={Paper}
            sx={{
                ...componentStyleProps.tableContainerSx,
                ...sx,
            }}>
            <Table sx={{ minWidth: 650, ...componentStyleProps.tableSx }} aria-label="simple table">
                <TableHead
                    sx={{
                        "&.MuiTableHead-root": {
                            backgroundColor: "background.paper",
                        },

                        "&.MuiTableHead-root .MuiTableCell-root": {
                            fontSize: "14px",
                            lineHeight: "18px",
                            fontWeight: 500,
                            color: config.textPrimary,
                            textTransform: "inherit",
                        },

                        ...componentStyleProps.tableHeadSx,
                    }}>
                    <TableRow>
                        {columns.map((currentColumn, index) => (
                            <TableCell
                                key={index}
                                align="left"
                                sx={{
                                    "& .MuiButtonBase-root.MuiTableSortLabel-root": {
                                        visibility: "hidden",
                                    },
                                    "&:hover .MuiButtonBase-root.MuiTableSortLabel-root": {
                                        visibility: "visible",
                                    },

                                    ...componentStyleProps.tableHeaderCellSx,
                                }}
                                {...currentColumn.headCellProps}>
                                {currentColumn.name}

                                <TableSortLabel
                                    IconComponent={ArrowUpward}
                                    active={orderBy === currentColumn.fieldName ? true : false}
                                    hideSortIcon={!currentColumn.showSortIcon}
                                    direction={order}
                                    onClick={(e) => {
                                        handleRequestSort(e, currentColumn.fieldName);
                                    }}
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody
                    sx={{
                        //   Table body rows css

                        "&.MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
                            borderBottom: "1px solid #E9E9E9",
                        },
                        "&.MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                        },
                        "&.MuiTableBody-root .MuiTableCell-root": {
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: 400,
                            color: "#374566",
                        },
                        ...(hoverEffect && {
                            "&.MuiTableBody-root .MuiTableRow-root:nth-child(odd)": {
                                backgroundColor: "#F8F9FA",
                            },
                            "&.MuiTableBody-root .MuiTableRow-root:hover": {
                                backgroundColor: "#E9ECEF",
                                transition: "background-color 0.1s ease-in-out", // Smooth transition
                            },
                        }),

                        ...componentStyleProps.tableBodySx,
                    }}>
                    {visibleRows?.length > 0 ? (
                        visibleRows.map((row, index) => {
                            return (
                                <TableRow
                                    key={index}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        onRowClickHandler(row);
                                    }}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    {columns.map((cColumn) => {
                                        return (
                                            <TableCell
                                                key={cColumn.fieldName}
                                                sx={{
                                                    ...componentStyleProps.tableBodyCellSx,
                                                }}>
                                                {!cColumn.hideCellValue &&
                                                    (typeof row[cColumn.fieldName] !== "object" ||
                                                        Array.isArray(row[cColumn.fieldName])) &&
                                                    row[cColumn.fieldName]}{" "}
                                                {cColumn.renderElement &&
                                                    cColumn.renderElement(row, row[cColumn.fieldName])}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell
                                align="center"
                                colSpan={columns?.length}
                                sx={(theme) => ({
                                    background: theme.palette.background.paper,
                                    color: theme.palette.text.secondary,
                                    borderColor: theme.palette.gray[200],
                                    fontSize: "14px", // 14px
                                    fontWeight: theme.typography.fontWeightRegular,
                                    lineHeight: "150%",
                                })}>
                                No Data
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
